/**
 * Quick PIN entry — R27 (2026-04-30)
 * 4-digit PIN authentication for ops-critical workspaces.
 * /pin — choose label + enter PIN → JWT minted + redirect to label's route.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

export default function QuickPin() {
  const navigate = useNavigate();
  const [labels, setLabels] = useState([]);
  const [label, setLabel] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    let alive = true;
    api.get("/quick-pin/labels").then((r) => {
      if (!alive) return;
      const list = r.data || [];
      setLabels(list);
      if (list.length && !label) setLabel(list[0].label);
    }).catch(() => {});
    return () => { alive = false; };
    
  }, []);
  // R30: scoped PWA manifest for Quick PIN access ("GO — off-platform" green icon).
  // When this page is added to the home screen, the operator gets a dedicated
  // green GO icon that launches straight back to /pin.
  useEffect(() => {
    const head = document.head;
    const created = [];
    const replaceLink = (selector, attrs) => {
      const existing = document.querySelector(selector);
      if (existing) existing.dataset.goPwaPrev = existing.outerHTML;
      const el = existing || document.createElement("link");
      if (!existing) created.push(el);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      if (!existing) head.appendChild(el);
      return el;
    };
    const replaceMeta = (selector, attrs) => {
      const existing = document.querySelector(selector);
      if (existing) existing.dataset.goPwaPrev = existing.outerHTML;
      const el = existing || document.createElement("meta");
      if (!existing) created.push(el);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      if (!existing) head.appendChild(el);
      return el;
    };
    const targets = [
      replaceLink("link[rel='manifest']", { rel: "manifest", href: "/manifest-go.webmanifest" }),
      replaceMeta("meta[name='theme-color']", { name: "theme-color", content: "#10B981" }),
      replaceMeta("meta[name='apple-mobile-web-app-title']", { name: "apple-mobile-web-app-title", content: "GO" }),
      replaceLink("link[rel='apple-touch-icon']", { rel: "apple-touch-icon", href: "/icons/icon-go-192x192.png" }),
    ];
    return () => {
      created.forEach((n) => { try { n.remove(); } catch { /* noop */ } });
      targets.forEach((n) => {
        if (n && n.dataset?.goPwaPrev) {
          const tmp = document.createElement("div");
          tmp.innerHTML = n.dataset.goPwaPrev;
          const orig = tmp.firstElementChild;
          if (orig) n.replaceWith(orig);
        }
      });
    };
  }, []);


  const fullPin = useMemo(() => pin.join(""), [pin]);
  const canSubmit = label && fullPin.length === 4 && !submitting;

  const updatePin = (i, v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 1);
    setPin((prev) => {
      const next = [...prev];
      next[i] = d;
      return next;
    });
    if (d && i < 3) refs[i + 1].current?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !pin[i] && i > 0) refs[i - 1].current?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const r = await api.post("/quick-pin/login", { label, pin: fullPin });
      const { token, redirect, user } = r.data || {};
      if (!token) throw new Error("no_token");
      localStorage.setItem("token", token);
      // R29: AuthContext requires BOTH token and user — store user from PIN response
      // so AdminRoute / ProtectedRoute don't bounce to /login (no double-login).
      if (user && user.id) {
        localStorage.setItem("user", JSON.stringify(user));
      }
      try {
        const claims = JSON.parse(atob(token.split(".")[1]));
        if (claims?.user_id) localStorage.setItem("userId", claims.user_id);
      } catch (_e) { /* noop */ }
      // R30 hotfix: full reload so AuthContext re-reads localStorage (fixes
      // ProtectedRoute/AdminRoute bouncing rohan/mark to /login after PIN auth).
      window.location.assign(redirect || "/hub");
    } catch (e2) {
      const msg = e2?.response?.status === 423
        ? "Too many wrong attempts. Locked for 15 minutes."
        : e2?.response?.status === 401
          ? "Invalid PIN."
          : "Could not sign in. Try again.";
      setError(msg);
      setPin(["", "", "", ""]);
      refs[0].current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e?.preventDefault();
    setForgotMsg("Sending…");
    try {
      const r = await api.post("/quick-pin/forgot", { label_or_email: forgotEmail || label });
      setForgotMsg(r?.data?.message || "If a matching account exists, a reset link was sent.");
    } catch {
      setForgotMsg("Could not send reset email — try again.");
    }
  };

  return (
    <div data-testid="quick-pin-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-emerald-300 text-xs uppercase tracking-[0.2em] font-semibold">Liantiao GO</div>
          <h1 className="text-3xl font-black mt-2">Quick PIN access</h1>
          <p className="text-slate-300 text-sm mt-1">Pick your workspace and enter your 4-digit PIN.</p>
        </div>

        <form onSubmit={submit} className="bg-white text-slate-900 rounded-2xl shadow-xl p-6 space-y-5" data-testid="quick-pin-form">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Workspace</label>
            <select
              data-testid="quick-pin-label"
              className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            >
              {labels.map((l) => (
                <option key={l.label} value={l.label}>{l.name} ({l.label})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">PIN</label>
            <div className="mt-1 flex gap-2 justify-center">
              {pin.map((d, i) => (
                <input
                  key={i}
                  data-testid={`quick-pin-digit-${i}`}
                  ref={refs[i]}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={d}
                  onChange={(e) => updatePin(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  className="w-14 h-14 text-center text-2xl font-black tracking-tight rounded-xl border-2 border-slate-300 bg-slate-50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                  autoFocus={i === 0}
                />
              ))}
            </div>
          </div>

          {error && (
            <div data-testid="quick-pin-error" className="text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="quick-pin-submit"
            disabled={!canSubmit}
            className={`w-full rounded-xl py-3 font-bold text-base ${canSubmit ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-200 text-slate-500"}`}
          >
            {submitting ? "Signing in…" : "Continue"}
          </button>

          <div className="text-center">
            <button
              type="button"
              data-testid="quick-pin-forgot-toggle"
              className="text-xs font-semibold text-emerald-700 hover:underline"
              onClick={() => setShowForgot((v) => !v)}
            >
              Forgot your PIN?
            </button>
          </div>

          {showForgot && (
            <div className="border-t border-slate-200 pt-4 space-y-2" data-testid="quick-pin-forgot-block">
              <p className="text-xs text-slate-600">Enter your email to receive a reset link.</p>
              <input
                data-testid="quick-pin-forgot-email"
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                data-testid="quick-pin-forgot-submit"
                onClick={submitForgot}
                className="w-full rounded-xl py-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold"
              >
                Send reset link
              </button>
              {forgotMsg && (
                <div data-testid="quick-pin-forgot-msg" className="text-xs text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  {forgotMsg}
                </div>
              )}
            </div>
          )}
        </form>

        <div className="mt-6 text-center text-xs text-slate-400">
          Need full account access? <a href="/login" className="text-emerald-300 hover:underline">Use the regular sign-in</a>.
        </div>
      </div>
    </div>
  );
}
